import { useState, useEffect, useContext, useCallback } from 'react';
import { format } from 'date-fns';
import { es, enUS, ca, pt, de, fr, Locale } from 'date-fns/locale';
import { Link } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
    AppContext,
    SttTable,
    StatusDot,
    SttPage,
} from '../../../sporttia/all';
import { SttTopFilteringControls } from '../../../sporttia/SttTopFilteringControls';
import SalePoint from '../salePoint/SalePoint';
import { formatPriceByLocale } from '../../../lib/utils';

const localeMap: Record<string, Locale> = {
    es,
    en: enUS,
    ca,
    pt,
    de,
    fr,
    mx: es,
};

type Filters = {
    ini: string;
    end: string;
};

type Ticket = {
    id: number;
    user?: {
        fullName?: string;
        img?: {
            url?: string;
        };
    };
    created: string;
    status: string;
    totalPaid: number;
    priceShape?: {
        currency?: string;
        locale?: string;
    };
};

type TicketsResponse = {
    rows: Ticket[];
    count: number;
    page: number;
};

type OpenedTicket = {
    id: number;
} | null;

export default function SalePointTickets(): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t, i18n } = useTranslation();
    const [tickets, setTickets] = useState<TicketsResponse>();
    const [openedTicket, setOpenedTicket] = useState<OpenedTicket>(null);

    const [filters, setFilters] = useState<Filters>({
        ini: '',
        end: format(new Date(), 'yyyy-MM-dd'),
    });

    const formatDate = useCallback(
        (date?: string): string => {
            if (!date) return '';
            const d = new Date(date);
            const locale = localeMap[i18n.language] || es;
            const isCurrentYear = d.getFullYear() === new Date().getFullYear();
            return format(d, isCurrentYear ? 'd MMM' : 'd MMM yyyy', {
                locale,
            });
        },
        [i18n.language],
    );

    /**
     * Load tickets with date filters
     */
    const loadTickets = (p = { page: 1 }, filtersParam?: Filters) => {
        const activeFilters = filtersParam || filters;
        const params = {
            page: p.page,
            rows: 20,
            ini: activeFilters.ini,
            end: activeFilters.end,
        };

        cxt?.api('GET', `/scs/${cxt?.sc?.id}/products/tickets`, {
            params,
            success: (r: TicketsResponse) => {
                setTickets(r);
                cxt?.setHeader({
                    title: t('Tickets'),
                });
            },
        });
    };

    /**
     * Handle filter changes from SttTopFilteringControls
     */
    const handleFilter = (newFilters: { ini?: string; end?: string }) => {
        const updatedFilters = {
            ini: newFilters.ini || '',
            end: newFilters.end || format(new Date(), 'yyyy-MM-dd'),
        };
        setFilters(updatedFilters);
        loadTickets({ page: 1 }, updatedFilters);
    };

    /**
     * SC changes
     */
    useEffect(() => {
        if (cxt?.sc?.id) loadTickets();
    }, [cxt?.sc?.id]);

    // Render
    return (
        <SttPage title={t('Tickets')}>
            <SttTopFilteringControls
                p={2}
                extraFields={[
                    {
                        name: 'ini',
                        type: 'date',
                        value: filters.ini,
                        caption: t('Dateini'),
                    },
                    {
                        name: 'end',
                        type: 'date',
                        value: filters.end,
                        caption: t('Dateend'),
                    },
                ]}
                onFilter={handleFilter}
                skipFirstFiltering
            />

            <SttTable
                data={tickets}
                autoload={false}
                columns={[
                    {
                        title: t('Status'),
                        align: 'center',
                        width: 60,
                        value: (row: Ticket) => (
                            <div className="flex justify-center w-full">
                                <StatusDot active={row.status === 'OPEN'} />
                            </div>
                        ),
                    },
                    {
                        title: t('Created'),
                        field: 'created',
                        value: (row: Ticket) => formatDate(row.created),
                    },
                    {
                        title: t('User'),
                        field: 'user',
                        align: 'left',
                        value: (row: Ticket) => (
                            <Link
                                underline="hover"
                                color="primary"
                                sx={{ cursor: 'pointer', fontWeight: 'bold' }}
                                onClick={() => setOpenedTicket({ id: row.id })}
                            >
                                {row.user?.fullName || t('ticket.anonymous')}
                            </Link>
                        ),
                    },
                    {
                        align: 'right',
                        title: t('Total'),
                        field: 'totalPaid',
                        value: (row: Ticket) =>
                            formatPriceByLocale(
                                row.totalPaid,
                                row.priceShape?.currency,
                                row.priceShape?.locale,
                            ),
                    },
                ]}
                onFetch={(p: { page: number }) => loadTickets(p)}
            />

            {openedTicket && (
                <SalePoint
                    open
                    idTicket={openedTicket.id}
                    onClose={() => {
                        setOpenedTicket(null);
                        loadTickets();
                    }}
                />
            )}
        </SttPage>
    );
}
